
import { DELETE_DEVICE_TYPE, SUBSCRIPTION_DEVICE_TYPES, VIEW_DEVICE_TYPES_BY_PROJECT_ID } from '../../schema/deviceTypes';
import { CREATE_VERSION, UPDATE_VERSION, VERSION_FILES, CREATE_VERSION_FILE, DELETE_VERSION_FILE } from '../../schema/versions';
import { CAPABILITIES, PAGINATED_CAPABILITIES, ENCODER, ENCODERS, PROJECT_ENCODERS, FUNCTIONS, FUNCTION, FUNCTION_EXECUTIONS, FUNCTION_EXECUTION_STATS, ENCODER_PROTOCOLS, CAPABILITY_ENCODERS, STORE_CAPABILITY_ENCODER, DELETE_CAPABILITY_ENCODER, STORE_CAPABILITY, UPDATE_CAPABILITY, DELETE_CAPABILITY, CREATE_ENCODER, UPDATE_ENCODER, DELETE_ENCODER } from '../../schema/capabilities';
import { assertTrustedUrl } from '../../utils/trustedUrls';

const APOLLO_OPTIONS = {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
};

/**
 * Runs an Apollo operation and unwraps the single root field the caller wants.
 *
 * `apollo.query()` / `apollo.mutate()` already return a promise that settles
 * exactly once, so there is nothing to re-wrap. Errors propagate as rejections
 * and are reported by the caller, which is the only place with enough context to
 * show a useful message.
 */
const unwrap = (promise, field) => promise.then(({ data }) => (data ? data[field] : null));

const query = (rootGetters, document, variables, field) =>
    unwrap(rootGetters.apollo.query({ query: document, variables, ...APOLLO_OPTIONS }), field);

const mutate = (rootGetters, document, variables, field) =>
    unwrap(rootGetters.apollo.mutate({ mutation: document, variables, ...APOLLO_OPTIONS }), field);

export default {
    namespaced: true,
    state: {
        device_types: [],
        loaded: false,
    },
    getters: {
    //Selected (current) project of the selected Business Unit
        device_types: (state) => {
            return state.device_types;
        },

        // True once load_device_types has completed, so views can tell "still
        // loading" apart from "this project genuinely has no device type".
        loaded: (state) => {
            return state.loaded;
        },

        device_type_by_id: (state) => (id) => {
            return state.device_types.find(item => item.id === id);
        },

        version_by_id: (state) => (device_type_id, id) => {
            let type = state.device_types.find(item => item.id === device_type_id);
            if (!type)
                return null;

            return type.versions.find(item => item.id === id);
        }
    },
    mutations: {
        update_device_types: (state, device_types) => {
            state.device_types = device_types;
        },
        set_loaded: (state, loaded) => {
            state.loaded = loaded;
        },
        update_device_type_by_id: (state, device_type) => {
            const index = state.device_types.findIndex(item => item.id === device_type.id);
            if (index !== -1) {
                state.device_types.splice(index, 1, device_type);
            } else {
                state.device_types.push(device_type);
            }
        },
        delete_device_type_by_id: (state, device_type_id) => {
            const index = state.device_types.findIndex(item => item.id === device_type_id);
            if (index !== -1) {
                state.device_types.splice(index, 1);
            }
        },
        update_version_by_id: (state, version) => {

            let type_index = state.device_types.findIndex(item => item.id === version.device_type_id);
            if (type_index === -1)
                return null;

            let index = state.device_types[type_index].versions.findIndex(item => item.id === version.id);
            if (index !== -1) {
                state.device_types[type_index].versions.splice(index, 1, version);
            } else {
                state.device_types[type_index].versions.push(version);
            }
        },
    },
    actions: {
        load_device_types: ({ commit, rootGetters }, payload) => {
            return new Promise((resolve, reject) =>{

                commit('set_loaded', false);

                rootGetters.apollo.query({
                    query: VIEW_DEVICE_TYPES_BY_PROJECT_ID,
                    variables: {
                        projectId: payload.project_id,
                        filters: { item_per_page: 1000 },
                        with: ['versions'],
                    },
                    fetchPolicy: 'no-cache',
                    notifyOnNetworkStatusChange: true
                }).then(({ data, loading }) => {
                    if (!loading && data !== undefined) {

                        commit('update_device_types', data.deviceTypesByProjectId.data);
                        commit('set_loaded', true);

                        rootGetters.apollo.subscribe({
                            query: SUBSCRIPTION_DEVICE_TYPES,
                            variables: {
                                projectId: payload.project_id
                            }
                        }).subscribe({
                            async next(message)  {
                                const device_type = message.data.liveDeviceType;

                                if (device_type.deleted_at && device_type.deleted_at !== '') {
                                    commit('delete_device_type_by_id', device_type.id);
                                } else
                                    commit('update_device_type_by_id', device_type);
                            },
                            error(error) {
                                console.error('error', error);
                            }
                        });

                    }
                    resolve();

                }).catch((error) => {
                    console.error(error.message);
                    reject(error);
                });
            });
        },

        delete: ({ commit, rootGetters }, payload) => {
            // The id comes from a route param, so it can be a string: the mutation
            // takes an Int! and the store indexes device types by numeric id.
            const id = parseInt(payload.id);

            return mutate(rootGetters, DELETE_DEVICE_TYPE, { id }, 'deleteDeviceType')
                .then((result) => {
                    commit('delete_device_type_by_id', id);
                    return result;
                });
        },

        storeOrUpdateVersion: ({ commit, rootGetters }, payload) => {
            const field = payload.create ? 'storeVersion' : 'updateVersion';

            return unwrap(rootGetters.apollo.mutate({
                mutation: payload.create ? CREATE_VERSION : UPDATE_VERSION,
                variables: {
                    id: !payload.create ? parseInt(payload.id) : null,
                    input: payload.version,
                },
            }), field).then((version) => {
                commit('update_version_by_id', version);
                return version;
            });
        },

        getVersionFiles: ({ rootGetters }, payload) => {
            return query(rootGetters, VERSION_FILES, payload, 'versionFiles')
                .then((result) => (result ? result.files : []));
        },

        storeVersionFile: ({ rootGetters }, payload) => {
            return mutate(rootGetters, CREATE_VERSION_FILE, {
                projectId: payload.projectId,
                deviceTypeId: payload.deviceTypeId,
                versionId: payload.versionId,
                input: payload.input,
            }, 'createVersionFile');
        },

        deleteVersionFile: ({ rootGetters }, payload) => {
            return mutate(rootGetters, DELETE_VERSION_FILE, {
                projectId: payload.projectId,
                deviceTypeId: payload.deviceTypeId,
                versionId: payload.versionId,
                fileName: payload.fileName,
            }, 'deleteVersionFile');
        },

        loadCapabilities: ({ rootGetters }, payload) => {
            return query(rootGetters, CAPABILITIES, {
                projectId: payload.projectId,
            }, 'capabilities');
        },

        loadPaginatedCapabilities: ({ rootGetters }, payload) => {
            return query(rootGetters, PAGINATED_CAPABILITIES, {
                projectId: payload.projectId,
                filters: payload.filters,
            }, 'paginatedCapabilities');
        },

        loadEncoders: ({ rootGetters }, payload) => {
            return query(rootGetters, ENCODERS, {
                projectId: payload.projectId,
            }, 'encoders');
        },

        loadEncoder: ({ rootGetters }, payload) => {
            return query(rootGetters, ENCODER, {
                projectId: payload.projectId,
                encoderId: payload.encoderId,
            }, 'encoder');
        },

        loadFunctions: ({ rootGetters }, payload) => {
            return query(rootGetters, FUNCTIONS, {
                projectId: payload.projectId,
            }, 'functions');
        },

        loadFunction: ({ rootGetters }, payload) => {
            return query(rootGetters, FUNCTION, {
                projectId: payload.projectId,
                functionId: payload.functionId,
            }, 'function');
        },

        loadFunctionExecutions: ({ rootGetters }, payload) => {
            return query(rootGetters, FUNCTION_EXECUTIONS, {
                projectId: payload.projectId,
                functionId: payload.functionId,
                filters: payload.filters,
            }, 'functionExecutions');
        },

        loadFunctionExecutionStats: ({ rootGetters }, payload) => {
            return query(rootGetters, FUNCTION_EXECUTION_STATS, {
                projectId: payload.projectId,
                functionId: payload.functionId,
            }, 'functionExecutionStats');
        },

        // uploadFunctionFile uploads a WASM binary directly to the public
        // functions API (api-gateway /v2), the same endpoint fundamentum-cli
        // uses. hub-gateway provides the fully-qualified file_upload_url, so the
        // origin is validated before the OAuth token is attached as a header.
        uploadFunctionFile: ({ rootGetters }, payload) => {
            const formData = new FormData();
            formData.append('function', payload.file);

            return fetch(assertTrustedUrl(payload.uploadUrl), {
                method: 'PUT',
                headers: {
                    'Authorization': rootGetters['oauth/authorization_token']
                },
                body: formData
            }).then((response) => {
                if (!response.ok) {
                    throw new Error(`Upload failed with status ${response.status}`);
                }
                return response;
            });
        },

        // invokeFunction runs the function against a raw input payload using the
        // public :invoke endpoint (same one devices use). The X-Function-* headers
        // give the encoder/decoder its execution context (mode, action, capability,
        // devices); the gateway forwards any header prefixed with "x-function-".
        // Returns the unwrapped invocation result ({ started_at, execution_time, output }).
        invokeFunction: ({ rootGetters }, payload) => {
            return fetch(assertTrustedUrl(payload.invokeUrl), {
                method: 'POST',
                headers: {
                    'Authorization': rootGetters['oauth/authorization_token'],
                    'Content-Type': 'application/json',
                    ...(payload.headers || {}),
                },
                body: payload.input,
            }).then(async (response) => {
                const text = await response.text();
                if (!response.ok) {
                    throw new Error(text || `Invocation failed with status ${response.status}`);
                }
                const parsed = text ? JSON.parse(text) : {};
                return parsed.data !== undefined ? parsed.data : parsed;
            });
        },

        loadProjectEncoders: ({ rootGetters }, payload) => {
            return query(rootGetters, PROJECT_ENCODERS, {
                projectId: payload.projectId,
                filters: payload.filters,
            }, 'projectEncoders');
        },

        loadCapabilityEncoders: ({ rootGetters }, payload) => {
            return query(rootGetters, CAPABILITY_ENCODERS, {
                projectId: payload.projectId,
                deviceTypeId: payload.deviceTypeId,
            }, 'capabilityEncoders');
        },

        storeCapabilityEncoder: ({ rootGetters }, payload) => {
            return mutate(rootGetters, STORE_CAPABILITY_ENCODER, {
                projectId: payload.projectId,
                capabilityId: payload.capabilityId,
                input: payload.input,
            }, 'storeCapabilityEncoder');
        },

        deleteCapabilityEncoder: ({ rootGetters }, payload) => {
            return mutate(rootGetters, DELETE_CAPABILITY_ENCODER, {
                projectId: payload.projectId,
                capabilityId: payload.capabilityId,
                capabilityEncoderId: payload.capabilityEncoderId,
            }, 'deleteCapabilityEncoder');
        },

        loadEncoderProtocols: ({ rootGetters }) => {
            return query(rootGetters, ENCODER_PROTOCOLS, {}, 'encoderProtocols');
        },

        // Creates the capability when no capabilityId is given, updates it otherwise.
        storeCapability: ({ rootGetters }, payload) => {
            const update = Boolean(payload.capabilityId);

            return mutate(rootGetters, update ? UPDATE_CAPABILITY : STORE_CAPABILITY, {
                projectId: payload.projectId,
                capabilityId: payload.capabilityId,
                input: payload.input,
            }, update ? 'updateCapability' : 'storeCapability');
        },

        deleteCapability: ({ rootGetters }, payload) => {
            return mutate(rootGetters, DELETE_CAPABILITY, {
                projectId: payload.projectId,
                capabilityId: payload.capabilityId,
            }, 'deleteCapability');
        },

        // Creates an encoder together with its backing function in a single
        // hub-gateway call. The input references an existing function
        // (function_id) or supplies a language so the gateway creates a new one.
        createEncoder: ({ rootGetters }, payload) => {
            return mutate(rootGetters, CREATE_ENCODER, {
                projectId: payload.projectId,
                input: payload.input,
            }, 'createEncoder');
        },

        // Takes the same input shape as createEncoder (CreateEncoderInput).
        updateEncoder: ({ rootGetters }, payload) => {
            return mutate(rootGetters, UPDATE_ENCODER, {
                projectId: payload.projectId,
                encoderId: payload.encoderId,
                input: payload.input,
            }, 'updateEncoder');
        },

        deleteEncoder: ({ rootGetters }, payload) => {
            return mutate(rootGetters, DELETE_ENCODER, {
                projectId: payload.projectId,
                encoderId: payload.encoderId,
            }, 'deleteEncoder');
        }

    }
};
